import { type FC } from "react";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { QuotaForecastingMinuteBucket } from "../../types/quotaForecasting";

import { useTheme } from "../../context/ThemeContext";
import { formatTokenCount } from "../../utils/currency";
import { ContentCard } from "../common/ContentCard";
import { SectionHeader } from "../common/SectionHeader";
import { Text } from "../common/Text";

export interface QuotaForecastingLoadChartProps {
  buckets: QuotaForecastingMinuteBucket[];
}

function formatMinuteLabel(timestampMs: number): string {
  return new Date(timestampMs).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export const QuotaForecastingLoadChart: FC<QuotaForecastingLoadChartProps> = ({
  buckets,
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  if (buckets.length === 0) {
    return (
      <div className="space-y-3">
        <SectionHeader title="Load" />
        <ContentCard>
          <Text>No minute-level quota telemetry is available yet.</Text>
        </ContentCard>
      </div>
    );
  }

  const chartData = buckets.map((bucket) => ({
    failedCount: bucket.failed_request_count,
    label: formatMinuteLabel(bucket.end_timestamp_ms),
    rollingRPM: bucket.rolling_requests_per_minute,
    rollingTPM: bucket.rolling_total_tokens_per_minute,
    throttledCount: bucket.throttled_request_count,
  }));

  return (
    <div className="space-y-3">
      <SectionHeader title="Load" />
      <ContentCard className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ left: 0, right: 12, top: 12 }}
          >
            <CartesianGrid
              stroke={isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb"}
              strokeDasharray="3 3"
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              yAxisId="rpm"
              orientation="left"
              allowDecimals={false}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              yAxisId="tpm"
              orientation="right"
              tickFormatter={(value) => formatTokenCount(value)}
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              formatter={(
                value: number | undefined,
                name: string | undefined,
              ) => {
                switch (name) {
                  case "rollingTPM": {
                    return [
                      `${(value ?? 0).toLocaleString()} tok/min`,
                      "Rolling TPM",
                    ];
                  }
                  case "rollingRPM": {
                    return [(value ?? 0).toLocaleString(), "Rolling RPM"];
                  }
                  case "throttledCount": {
                    return [(value ?? 0).toLocaleString(), "Throttled / Min"];
                  }
                  case "failedCount": {
                    return [(value ?? 0).toLocaleString(), "Failed / Min"];
                  }
                  default: {
                    return [(value ?? 0).toLocaleString(), name ?? "Value"];
                  }
                }
              }}
            />
            <Bar
              yAxisId="rpm"
              dataKey="throttledCount"
              name="throttledCount"
              fill="rgba(245, 158, 11, 0.72)"
              radius={[3, 3, 0, 0]}
            />
            <Bar
              yAxisId="rpm"
              dataKey="failedCount"
              name="failedCount"
              fill="rgba(239, 68, 68, 0.72)"
              radius={[3, 3, 0, 0]}
            />
            <Area
              yAxisId="tpm"
              type="monotone"
              dataKey="rollingTPM"
              name="rollingTPM"
              stroke="#0f766e"
              fill="rgba(20, 184, 166, 0.18)"
              strokeWidth={2}
            />
            <Line
              yAxisId="rpm"
              type="monotone"
              dataKey="rollingRPM"
              name="rollingRPM"
              stroke="#1d4ed8"
              dot={false}
              strokeWidth={2}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ContentCard>
    </div>
  );
};
